<template>
  <div class="min-h-screen bg-slate-100 py-8 px-4">
    <div class="mx-auto w-full max-w-2xl">
      <div v-if="isLoading" class="bg-white rounded-2xl shadow-sm p-10 text-center text-pdsPrimaryColor">
        <p>Bezig met laden...</p>
      </div>

      <div v-else-if="isSaved" class="bg-white rounded-2xl shadow-sm p-8 md:p-10 text-center">
        <div class="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-green-500">
          <CheckIcon class="h-8 w-8 text-white" aria-hidden="true" />
        </div>

        <h1 class="text-2xl md:text-3xl font-bold text-slate-900 mb-3">Toestemming opgeslagen!</h1>
        <p class="text-slate-500 text-sm leading-relaxed mb-8">
          Uw voorkeuren zijn succesvol opgeslagen. U kunt dit venster sluiten.
        </p>

        <div v-if="savedSharedDepartments.length" class="rounded-xl bg-slate-50 border border-slate-100 p-5 mb-8 text-left">
          <p class="text-xs font-semibold uppercase tracking-wide text-sky-700/80 mb-4">Gedeelde afdelingen</p>
          <ul class="space-y-3">
            <li
              v-for="department in savedSharedDepartments"
              :key="department.departmentId"
              class="flex items-center gap-2 text-sm font-medium text-slate-800"
            >
              <CheckIcon class="h-4 w-4 text-green-500 shrink-0" aria-hidden="true" />
              <span>{{ department.departmentName }}</span>
            </li>
          </ul>
        </div>

        <button
          type="button"
          class="mx-auto inline-flex min-w-[8rem] justify-center rounded-full border border-primary-default bg-white px-8 py-2.5 text-sm font-semibold text-primary-default hover:bg-slate-50"
          @click="closePage"
        >
          Sluiten
        </button>
      </div>

      <div v-else-if="tokenValid" class="bg-white rounded-2xl shadow-sm p-8 md:p-10">
        <h1 class="text-2xl md:text-3xl font-bold text-pdsPrimaryColor mb-4">Verzoek tot gegevens delen</h1>

        <p class="text-slate-600 text-sm leading-relaxed mb-2">
          Een zorgverlener heeft u verzocht om toestemming te geven voor het delen van uw gegevens. U kunt hieronder
          per afdeling aangeven welke gegevens gedeeld mogen worden.
        </p>
        <p class="text-slate-600 text-sm leading-relaxed mb-8">
          Uw keuzes worden direct opgeslagen zodra u op "Wijzigingen opslaan" klikt.
        </p>

        <div class="border border-slate-200 rounded-xl p-5 mb-8">
          <p class="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-4">Verzoek van:</p>
          <div class="flex items-start gap-4">
            <div
              class="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-slate-100 text-sm font-semibold text-pdsPrimaryColor"
            >
              {{ prescriberInitials }}
            </div>
            <div>
              <p class="text-base font-semibold text-slate-900">{{ prescriberDisplayName }}</p>
              <p v-if="institutionName" class="text-sm text-slate-600 mt-1">{{ institutionName }}</p>
              <span
                v-if="requestingDepartmentName"
                class="inline-block mt-2 rounded-full bg-sky-100 px-3 py-1 text-xs font-medium text-sky-800"
              >
                Afd. {{ requestingDepartmentName }}
              </span>
            </div>
          </div>
        </div>

        <div class="mb-6">
          <h2 class="text-lg font-semibold text-slate-900 mb-4">Toestemming per afdeling</h2>

          <div
            class="flex items-center justify-between gap-4 rounded-xl border border-teal-200 bg-teal-50/40 px-4 py-4 mb-4"
          >
            <p class="text-sm font-medium text-slate-800">Toestemming geven voor alle afdelingen</p>
            <Toggle :initial-value="allDepartmentsEnabled" :overwrite-value="allDepartmentsEnabled" @enable-status="toggleAllDepartments" />
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div
              v-for="department in departments"
              :key="department.departmentId"
              class="flex items-center justify-between gap-3 rounded-xl border border-slate-200 px-4 py-4"
            >
              <div class="min-w-0">
                <p class="text-sm font-semibold text-slate-900 truncate">{{ department.departmentName }}</p>
                <p class="text-xs text-slate-500 truncate">{{ department.institutionName }}</p>
              </div>
              <Toggle
                :initial-value="department.isSharingEnabled"
                :overwrite-value="department.isSharingEnabled"
                @enable-status="(value) => setDepartmentSharing(department.departmentId, value)"
              />
            </div>
          </div>
        </div>

        <button
          type="button"
          class="w-full rounded-full bg-primary-default py-3 text-sm font-semibold text-white hover:bg-primary-default/90 disabled:opacity-60"
          :disabled="isSubmitting"
          @click="saveChanges"
        >
          {{ isSubmitting ? 'Bezig met opslaan...' : 'Wijzigingen opslaan' }}
        </button>

        <p v-if="submitError" class="text-red-600 text-sm text-center mt-4">{{ submitError }}</p>
        <p class="text-xs text-slate-400 text-center mt-6">Uw privacy is beschermd.</p>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, ref, getCurrentInstance } from 'vue';
import { CheckIcon } from '@heroicons/vue/24/solid';
import router from '@/router';
import axios from 'axios';

export default {
  name: 'CustomerDataSharingApproval',
  components: {
    CheckIcon
  },
  setup() {
    const instance = getCurrentInstance();
    const token = ref(router.currentRoute.value.params.token);
    const isLoading = ref(true);
    const tokenValid = ref(false);
    const isSaved = ref(false);
    const isSubmitting = ref(false);
    const submitError = ref('');
    const patientName = ref('');
    const prescriberName = ref('');
    const prescriberInitials = ref('');
    const institutionName = ref('');
    const requestingDepartmentName = ref('');
    const departments = ref([]);
    const savedSharedDepartments = ref([]);

    const prescriberDisplayName = computed(() => {
      if (!prescriberName.value) {
        return 'Voorschrijver';
      }

      return prescriberName.value.startsWith('Dr.') ? prescriberName.value : `Dr. ${prescriberName.value}`;
    });

    const allDepartmentsEnabled = computed(() => {
      return departments.value.length > 0 && departments.value.every((department) => department.isSharingEnabled);
    });

    const mapDepartment = (department) => ({
      departmentId: department.DepartmentId ?? department.departmentId,
      departmentName: department.DepartmentName ?? department.departmentName ?? '',
      institutionName: department.InstitutionName ?? department.institutionName ?? '',
      isSharingEnabled: department.IsSharingEnabled ?? department.isSharingEnabled ?? false
    });

    const validateToken = async () => {
      try {
        const response = await axios.post('Customer/ValidateDataSharingApprovalToken', { Token: token.value });
        const data = response?.data || {};

        patientName.value = data.PatientName || data.patientName || '';
        prescriberName.value = data.PrescriberName || data.prescriberName || '';
        prescriberInitials.value = data.PrescriberInitials || data.prescriberInitials || '?';
        institutionName.value = data.InstitutionName || data.institutionName || '';
        requestingDepartmentName.value = data.RequestingDepartmentName || data.requestingDepartmentName || '';

        const departmentItems = data.Departments || data.departments || [];
        departments.value = departmentItems.map(mapDepartment);
        tokenValid.value = true;
      } catch (error) {
        expiredTokenRedirect(error);
      } finally {
        isLoading.value = false;
      }
    };

    const setDepartmentSharing = (departmentId, enabled) => {
      departments.value = departments.value.map((department) =>
        department.departmentId === departmentId ? { ...department, isSharingEnabled: enabled } : department
      );
    };

    const toggleAllDepartments = (enabled) => {
      departments.value = departments.value.map((department) => ({
        ...department,
        isSharingEnabled: enabled
      }));
    };

    const saveChanges = async () => {
      isSubmitting.value = true;
      submitError.value = '';

      try {
        await axios.post('Customer/ApproveDataSharingApprovalToken', {
          Token: token.value,
          Changes: departments.value.map((department) => ({
            DepartmentId: department.departmentId,
            IsSharingEnabled: department.isSharingEnabled
          }))
        });

        savedSharedDepartments.value = departments.value.filter((department) => department.isSharingEnabled);
        isSaved.value = true;
        tokenValid.value = false;
      } catch (error) {
        submitError.value =
          error?.response?.data?.Message ||
          error?.response?.data?.message ||
          'Het opslaan van uw voorkeuren is niet gelukt. Probeer het later opnieuw.';
      } finally {
        isSubmitting.value = false;
      }
    };

    const expiredTokenRedirect = (error) => {
      instance?.appContext.config.globalProperties.$emitter?.emit('alert', {
        message:
          error?.response?.data?.Message ||
          error?.response?.data?.message ||
          'De link is ongeldig of verlopen. De link is slechts 8 uur na verzending geldig.',
        type: 3,
        delay: 6000
      });
      router.push({ path: '/' });
    };

    const closePage = () => {
      router.push({ path: '/' });
    };

    validateToken();

    return {
      isLoading,
      tokenValid,
      isSaved,
      isSubmitting,
      submitError,
      patientName,
      prescriberName,
      prescriberInitials,
      prescriberDisplayName,
      institutionName,
      requestingDepartmentName,
      departments,
      savedSharedDepartments,
      allDepartmentsEnabled,
      setDepartmentSharing,
      toggleAllDepartments,
      saveChanges,
      closePage
    };
  },
  created() {
    if (this.$parent?.$parent) {
      this.$parent.$parent.pageTitle = '';
      this.$parent.$parent.navigationBackText = '';
    }
  }
};
</script>
